import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { GenerativeAvatar, Spinner } from "@/shared/ui";
import type { Assistant } from "../api/getAssistant";
import { useAssistantStarters } from "../hooks/useChatAssistant";

/**
 * The welcome hero when a new chat is scoped to an assistant: the assistant's
 * icon + name + description, the shared composer, and generated conversation
 * starters that pre-fill the composer. Mirrors {@link WelcomeState}'s shape.
 */
export function AssistantWelcome({
  assistant,
  composer,
  onChipSelect,
}: {
  assistant: Assistant;
  composer: ReactNode;
  onChipSelect: (prompt: string) => void;
}) {
  const { t } = useTranslation();
  const starters = useAssistantStarters(assistant);

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-[720px] text-center">
        <div className="mb-5 flex justify-center">
          {assistant.icon_url ? (
            <img
              src={assistant.icon_url}
              alt=""
              className="size-16 rounded-2xl object-cover"
            />
          ) : (
            <GenerativeAvatar seed={assistant.name} className="size-16 rounded-2xl" />
          )}
        </div>
        <h1 className="mb-3 font-serif text-[clamp(30px,4.6vw,46px)] font-normal leading-[1.05] tracking-[-0.02em] text-ink">
          {assistant.name}
        </h1>
        {assistant.description ? (
          <p className="mx-auto mb-9 max-w-[480px] font-serif text-[17px] font-normal leading-[1.5] text-ink-muted">
            {assistant.description}
          </p>
        ) : (
          <div className="mb-9" />
        )}

        <div className="text-left">{composer}</div>

        <div className="mt-5 min-h-[40px]">
          {starters.isLoading ? (
            <span className="inline-flex items-center gap-2 font-sans text-[13px] text-ink-faint">
              <Spinner size={14} /> {t("chat.assistant.generating")}
            </span>
          ) : starters.data && starters.data.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-2">
              {starters.data.slice(0, 4).map((starter, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onChipSelect(starter)}
                  className="inline-flex max-w-[320px] items-center gap-2 rounded-pill bg-bubble px-4 py-2 text-left font-sans text-[13.5px] font-medium text-ink outline-none transition-[background,transform,color] duration-150 ease-house hover:-translate-y-px hover:bg-tint hover:text-ink focus-visible:bg-tint"
                >
                  <span className="truncate">{starter}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <p className="mt-9 font-sans text-[12px] tracking-[0.04em] text-ink-faint">
          {t("chat.composer.disclaimer")}
        </p>
      </div>
    </div>
  );
}
